import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, ReplaySubject, take, tap } from 'rxjs';
import {
  AvailableClaimType,
  ManageMyRepairResponse,
  MMRItem,
  ProductAttribute,
  ProductTypeQuestion,
  RepairHistoryResponse,
  RepairHistoryResponseResult,
  AddressBlock,
} from '../types/manage-my-repair-types';
import { CONFIG } from '@domgen/dgx-fe-config';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AppliancesService } from './appliances.service';
import { ClaimType, RepairActionEnum } from '../types/plan-detail.types';
import { BundleResponse } from '../types/bundle.model';
import { CrossSell } from '../types/appliance.model';
import { ClaimsRedirectService } from './claims-redirect.service';
import { AppConfig } from '../types/app-config.model';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../types/user.model';
import jwtDecode from 'jwt-decode';
import { ClaimTypeText } from './claim-type-images';
import { FeatureFlagService } from '@domgen/dgx-components';

enum AuthHeaderNames {
  REQUEST_SOURCE = 'request-source',
  REQUEST_ACTION = 'request-action',
  CLIENT = 'wl-client',
}

@Injectable()
export class ManageMyRepairService {
  headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-response-version': '2',
    [AuthHeaderNames.REQUEST_SOURCE]: 'DandGUK',
    [AuthHeaderNames.REQUEST_ACTION]: 'MyAccount',
    [AuthHeaderNames.CLIENT]: 'DandGUK',
  };

  httpOptions = {
    headers: new HttpHeaders({
      ...this.headers,
    }),
    withCredentials: true,
  };

  planClaimsCache: Record<
    string,
    ReplaySubject<{
      loading: boolean;
      data: ManageMyRepairResponse;
      lastUpdated: Date | undefined;
    }>
  > = {};

  repairHistoryCache: Record<
    string,
    ReplaySubject<{ loading: boolean; data: RepairHistoryResponse | undefined }>
  > = {};

  schemaTelephoneNumbers: { keys: string; value: string }[] =
    this.translateService.instant('MMR.tel_numbers');

  user$: ReplaySubject<User> = new ReplaySubject<User>(1);
  userData: User | undefined;

  whiteListedClaimTypes = ['AD', 'BD', 'ASV'];

  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    private readonly http: HttpClient,
    private readonly applianceService: AppliancesService,
    private readonly claimsRedirectService: ClaimsRedirectService,
    private readonly translateService: TranslateService,
    private featureFlagService: FeatureFlagService
  ) {
    this.getApplicationUser();
  }

  getApplicationUser(): void {
    const userToken = this.getCookie('AWS_IDT');

    if (!userToken) {
      return;
    }

    const user = jwtDecode(userToken as string) as User;
    this.user$.next(user);
    this.user$.pipe(take(1)).subscribe({
      next: (user) => {
        this.userData = user;
      },
    });
  }

  private getCookie(name: string): string | undefined {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();

    return undefined;
  }

  getPlanClaims(planNumber: string): Observable<{
    loading: boolean;
    data: ManageMyRepairResponse;
    lastUpdated: Date | undefined;
  }> {
    if (!this.planClaimsCache[planNumber]) {
      this.planClaimsCache[planNumber] = new ReplaySubject();
      this.planClaimsCache[planNumber].next({
        loading: true,
        data: undefined as any,
        lastUpdated: undefined,
      });
    }

    this.dispatchGetPlanClaims(planNumber);

    return this.planClaimsCache[planNumber];
  }

  getPlanHistory(
    planNumber: string,
    openRepair: boolean
  ): Observable<{ loading: boolean; data: RepairHistoryResponse | undefined }> {
    if (!this.repairHistoryCache[planNumber]) {
      this.repairHistoryCache[planNumber] = new ReplaySubject();
      this.repairHistoryCache[planNumber].next({
        loading: true,
        data: undefined,
      });
    }

    this.dispatchGetPlanHistory(planNumber, openRepair);

    return this.repairHistoryCache[planNumber];
  }

  dispatchGetPlanHistory(planNumber: string, openRepair: boolean): void {
    const url = `${this.config.myAccountApi}/get-plan-history`;

    this.repairHistoryCache[planNumber].pipe(take(1)).subscribe({
      next: (data) => {
        this.repairHistoryCache[planNumber].next({
          data: data.data,
          loading: true,
        });
      },
    });

    this.http
      .post<{ result: RepairHistoryResponseResult[]; status: string }>(
        url,
        {
          planNumber,
        },
        this.httpOptions
      )
      .subscribe({
        next: (data) => {
          const resultData = this.processResultData(
            [...data.result],
            openRepair
          );
          this.repairHistoryCache[planNumber].next({
            loading: false,
            data: {
              result: resultData,
              status: data.status,
            },
          });
        },
        error: (error) => {
          this.repairHistoryCache[planNumber].error(error);
        },
      });
  }

  private processResultData(
    result: RepairHistoryResponseResult[],
    openRepair: boolean
  ): RepairHistoryResponseResult[] {
    if (openRepair && result.length > 0) {
      return result.slice(1);
    }
    return result;
  }

  submitFeedback(
    feedbackValue: Record<string, any>,
    planNumber?: string,
    address?: AddressBlock
  ): void {
    if (
      this.featureFlagService.isFeatureFlagEnabled(
        'mmr-feedback-capture',
        'mmrFeedbackCapture'
      ) &&
      feedbackValue.questions?.[0].answer !== 'Yes'
    ) {
      const body = {
        address: {
          countryCode: 'GB',
          addressLine1: address?.streetName || '',
          addressLine2: address?.localArea || '',
          addressLine3: address?.townCity || '',
          addressLine4: '',
          postalCode: address?.postCode,
        },
        email: this.userData.email,
        firstName: this.userData.name,
        planNumber: planNumber,
        query: {
          topic: 'UNHAPPY',
          questions: [
            {
              question: 'UNHAPPY_DETAIL',
              message: feedbackValue.questions?.[1]?.answer,
            },
          ],
        },
        surname: this.userData.family_name,
      };
      let path = '';
      if (this.config.environment !== 'prod') {
        path = '/v2';
      }
      const url = `${this.config.common}${path}/contact-us`;
      this.http.post(url, body).subscribe();
    }
    const url = `${this.config.myAccountApi}/submit-feedback`;
    this.http.post(url, feedbackValue, this.httpOptions).subscribe();
  }

  dispatchGetPlanClaims(planNumber: string): void {
    const url = `${this.config.myAccountApi}/get-plan-claims`;
    const requestedDate = new Date();

    this.planClaimsCache[planNumber].pipe(take(1)).subscribe({
      next: (data) => {
        this.planClaimsCache[planNumber].next({
          data: data.data,
          loading: true,
          lastUpdated: data.lastUpdated,
        });
      },
    });

    this.http
      .post<{ result: ManageMyRepairResponse; status: string }>(
        url,
        {
          planNumber,
        },
        this.httpOptions
      )
      .subscribe({
        next: (data) => {
          const planSchemaCode = planNumber.substring(0, 3);
          this.schemaTelephoneNumbers =
            this.translateService.instant('MMR.tel_numbers');

          const directCallCentreCode = this.schemaTelephoneNumbers.find(
            (number) => number.keys.indexOf(planSchemaCode) > -1
          );

          if (directCallCentreCode) {
            data.result.repairAction = RepairActionEnum.DIRECT_CALL_CENTRE;
            data.result.directReferral = {
              company_phone: directCallCentreCode.value,
            };
          }

          data.result.productTypeQuestion =
            this.mapAttributesToProductTypeQuestion(data.result.attributes);

          this.planClaimsCache[planNumber].next({
            loading: false,
            data: data.result,
            lastUpdated: requestedDate,
          });
        },
        error: (error) => {
          this.planClaimsCache[planNumber].error(error);
        },
      });
  }

  rebookClaim(
    planNumber: string,
    claimId: string,
    claimType: ClaimType,
    crossSell: CrossSell | null,
    timeslot?: 'AM' | 'PM'
  ): Observable<BundleResponse> {
    return this.applianceService
      .getRebookClaimBundle(planNumber, claimId, claimType, crossSell)
      .pipe(
        tap((val) => {
          this.claimsRedirectService.redirect(val, timeslot);
        })
      );
  }

  createClaim(
    planNumber: string,
    claimType: ClaimType,
    crossSell: CrossSell | null,
    mmrItem?: MMRItem
  ): Observable<any> {
    return this.applianceService
      .getClaimBundle(planNumber, claimType, crossSell)
      .pipe(
        tap((val) => {
          this.claimsRedirectService.redirect(val, undefined, mmrItem);
        })
      );
  }

  cancelClaim(planNumber: string, claimId: string): Observable<any> {
    return this.applianceService.cancelRepair(planNumber, claimId);
  }

  getClaimTypes(avaliableClaimTypes: AvailableClaimType[]): any[] {
    return avaliableClaimTypes
      .filter((ct) => this.whiteListedClaimTypes.includes(ct.claimTypeCode))
      .map((claimType) => {
        const ctLowerCase = claimType.claimTypeCode.toLowerCase();
        return {
          ...claimType,
          value: claimType.claimTypeCode,
          text:
            ClaimTypeText[claimType.claimTypeCode] ?? claimType.claimTypeLabel,
          image: `./assets/svg/desktop_${ctLowerCase}_default.svg`,
          activeImage: `./assets/svg/desktop_${ctLowerCase}_fill.svg`,
          currentImage: `./assets/svg/desktop_${ctLowerCase}_default.svg`,
          mobileImage: `./assets/svg/mobile_${ctLowerCase}_default.svg`,
          mobileActiveImage: `./assets/svg/mobile_${ctLowerCase}_fill.svg`,
          currentMobileImage: `./assets/svg/mobile_${ctLowerCase}_default.svg`,
        };
      });
  }

  startNewClaimsJourney(
    claimType: string,
    currentState: string,
    planNumber: string,
    hasServiceOption: boolean,
    claimTypeId: string,
    productTypeQuestion?: ProductTypeQuestion
  ): Observable<any> {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/start-claim`;
    const request: {
      claimType: string;
      currentState: string;
      planNumber: string;
      hasServiceOption: boolean;
      claimTypeId: string;
      productTypeQuestion?: {
        attributeLabel?: string;
        selectedOption: string;
      };
    } = {
      claimType,
      currentState,
      planNumber,
      hasServiceOption,
      claimTypeId,
    };

    const selectedProductType = productTypeQuestion?.selectedOption;

    if (selectedProductType) {
      request.productTypeQuestion = {
        attributeLabel: productTypeQuestion?.attributeLabel || undefined,
        selectedOption: selectedProductType,
      };
    }

    // TODO: redirect user into orbit claims flow from this point with bundle being set in session storage
    return this.http.post(url, request);
  }

  inspectBundle(bundle: string) {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/inspect-bundle`;
    const request = {
      bundle,
    };

    return this.http.post(url, request);
  }

  getData(
    searchTerm: string,
    dataType: string,
    dataCategory: string,
    GUID: string
  ) {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/GetData`;
    const request = {
      searchTerm,
      dataType,
      dataCategory,
      GUID,
    };

    return this.http.post(url, request);
  }
  // GetData endpoint
  // searchTerm: string,
  // dataType: string (comes from getMandatoryData)
  // dataCategory: string (comes from getMandatoryData)
  // GUID: string (comes from startTransaction)

  private mapAttributesToProductTypeQuestion(
    attributes?: ProductAttribute[] | null
  ): ProductTypeQuestion | undefined {
    const attributeList = attributes ?? [];

    if (attributeList.length === 0) {
      return undefined;
    }

    const [productTypeAttribute] = attributeList;
    const allowedValues = (productTypeAttribute.allowedValues ?? [])
      .map((value) => value?.trim())
      .filter((value): value is string => !!value);
    const isCombinationAppliance =
      !!productTypeAttribute.isCombinationAppliance;

    return {
      options: allowedValues,
      attributeLabel: productTypeAttribute.label,
      isCombinationAppliance,
    };
  }
}
